import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config';
import { useWebSocket } from '../hooks/useWebSocket';
import { useNotification, Notification as NotificationComponent } from './Notification';
import { formatDate } from '../utils/helpers';
import { Match } from '../types';
import { logger } from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../lib/analytics';

const JoinMatch: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState<Match | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    position: user?.position || 'Mediocampista',
    rating: user?.rating || 3,
    isSubstitute: false
  });
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [guestLeavePath, setGuestLeavePath] = useState<string | null>(null);
  const [showAppBanner, setShowAppBanner] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();
  const hasTrackedView = useRef(false);

  interface GroupContext {
    id: string;
    name: string;
    description?: string;
    memberCount: number;
    pendingMembers: Array<{
      id: string;
      guestName: string;
      guestPosition: string;
      guestRating: number;
      totalMatches: number;
      totalGoals: number;
      totalAssists: number;
    }>;
  }
  const [groupContext, setGroupContext] = useState<GroupContext | null>(null);

  // Detectar mobile UA y mostrar banner "abrir en app" si no fue dismisseado
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ua = window.navigator.userAgent || '';
    const isMobile = /iPhone|iPad|iPod|Android/i.test(ua);
    const dismissed = window.localStorage.getItem('joinAppBannerDismissed') === '1';
    if (isMobile && !dismissed) {
      setShowAppBanner(true);
      track('invite_app_prompted', { match_id: id, platform: 'web' });
    }
  }, [id]);

  const handleOpenInApp = () => {
    if (id) window.location.href = `futbolero://join/${id}`;
  };
  const handleDismissAppBanner = () => {
    setShowAppBanner(false);
    try {
      window.localStorage.setItem('joinAppBannerDismissed', '1');
    } catch {}
  };

  // WebSocket hook para actualizaciones en tiempo real
  const { isConnected, joinMatch, leaveMatch } = useWebSocket(
    (event) => {
      logger.websocket('Evento WebSocket recibido en JoinMatch:', event);
      
      switch (event.type) {
        case 'INITIAL_DATA':
        case 'PLAYER_ADDED':
        case 'SUBSTITUTE_ADDED':
        case 'PLAYER_REMOVED':
        case 'SUBSTITUTE_REMOVED':
        case 'MATCH_CREATED':
        case 'PLAYERS_SWAPPED':
        case 'TEAMS_GENERATED':
        case 'TEAM_NAMES_UPDATED':
        case 'PLAYER_REPLACED':
        case 'PLAYER_REMOVED_BY_ADMIN':
        case 'SUBSTITUTE_PROMOTED':
        case 'PLAYER_SELF_REMOVED':
        case 'SUBSTITUTE_SELF_REMOVED':
        case 'VOTE_CAST':
        case 'OWNERSHIP_TRANSFERRED':
          if (event.data.match) {
            setMatch(event.data.match);
            logger.websocket('Partido actualizado via WebSocket en JoinMatch:', event.type);
          }
          break;
        
        default:
          logger.websocket('Evento no manejado en JoinMatch:', event.type);
      }
    },
    (error) => {
      logger.error('Error WebSocket en JoinMatch:', error);
    }
  );

  const fetchMatch = useCallback(async () => {
    try {
      const response = await fetch(getApiUrl(`/api/matches/${id}`));
      const data = await response.json();
      setMatch(data);
    } catch (error) {
      logger.error('Error fetching match:', error);
      showNotification('Error al cargar el partido', 'error');
    }
  }, [id, showNotification]);

  const fetchGroupContext = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(getApiUrl(`/api/matches/${id}/group-context`));
      const data = await res.json().catch(() => ({}));
      setGroupContext(data.group || null);
    } catch {
      // non-critical
    }
  }, [id]);

  // Crear una referencia estable para el match ID actual
  const currentMatchIdRef = useRef<string | null>(null);
  
  // Efecto para unirse al WebSocket solo cuando cambie el ID
  useEffect(() => {
    if (!id) return;
    
    // Solo unirse si no estamos ya conectados a este match
    if (currentMatchIdRef.current !== id) {
      // Dejar el match anterior si existe
      if (currentMatchIdRef.current) {
        leaveMatch(currentMatchIdRef.current);
      }
      
      // Unirse al nuevo match
      joinMatch(id);
      currentMatchIdRef.current = id;
    }
    
    // Limpiar al desmontar
    return () => {
      if (currentMatchIdRef.current) {
        leaveMatch(currentMatchIdRef.current);
        currentMatchIdRef.current = null;
      }
    };
  }, [id, joinMatch, leaveMatch]);

  // Intent to join: only saved when user explicitly clicks "Me Sumo" (not on mount)
  const saveJoinIntent = () => {
    localStorage.setItem('joinMatchAfterLogin', JSON.stringify({
      matchId: id,
      matchLocation: match?.location,
      matchDate: match?.date,
      matchTime: match?.time,
    }));
  };

  // Actualizar formulario cuando el usuario cambie
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        position: user.position || 'Mediocampista',
        rating: user.rating || 3
      }));
    }
  }, [user]);

  // Auto-join: if user just completed auth after clicking "Me Sumo", join automatically
  useEffect(() => {
    if (user && id && match) {
      const joinData = localStorage.getItem('joinMatchAfterLogin');
      if (joinData) {
        localStorage.removeItem('joinMatchAfterLogin');
        // Check if already in match
        const alreadyIn = match.players?.some((p: any) => p.userId === user.id);
        if (alreadyIn) {
          navigate(`/match/${id}`);
          return;
        }
        // Auto-join with user profile data
        const maxTotal = match.maxTotalPlayers || match.maxPlayers * 2;
        const isFull = match.players?.filter((p: any) => !p.isSubstitute).length >= maxTotal;
        const token = localStorage.getItem('authToken');
        fetch(getApiUrl(`/api/matches/${id}/join`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            name: user.name || 'Jugador',
            position: user.position || 'Mediocampista',
            rating: user.rating || 3,
            isSubstitute: isFull,
          }),
        }).then(async (res) => {
          if (res.ok) {
            const result = await res.json();
            if (result.leaveLink && result.player) {
              localStorage.setItem(`player_${result.player.id}_leaveLink`, result.leaveLink);
              localStorage.setItem(`player_${result.player.id}_name`, result.player.name);
              localStorage.setItem(`player_${result.player.id}_matchId`, id || '');
            }
            showNotification(isFull ? 'Estás en la lista de espera' : '¡Estás dentro!', 'success');
            setTimeout(() => navigate(`/match/${id}`), 800);
          } else {
            const err = await res.json();
            showNotification(err.error || 'Error al unirse', 'error');
          }
        }).catch(() => {
          showNotification('Error al unirse al partido', 'error');
        });
      }
    }
  }, [user, id, match, navigate, showNotification]);

  // Al montar, verificar si el usuario o invitado ya está en el partido
  useEffect(() => {
    if (!id) return;
    const checkAlreadyJoined = async () => {
      let guestId = null;
      // Buscar leaveToken de este partido en localStorage (para invitados)
      const leaveTokenKey = Object.keys(localStorage).find(key => key.startsWith('player_') && localStorage.getItem(key + '_matchId') === id);
      if (leaveTokenKey) {
        // leaveToken es el token en el leaveLink
        const leaveLink = localStorage.getItem(leaveTokenKey + '_leaveLink');
        if (leaveLink) {
          const parts = leaveLink.split('/');
          guestId = parts[parts.length - 1];
        }
      }
      if (process.env.NODE_ENV === 'development') {

      }
      try {
        const headers: Record<string, string> = {};
        if (guestId) headers['x-guest-id'] = guestId;
        const token = localStorage.getItem('authToken');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (process.env.NODE_ENV === 'development') {
  
        }
        const res = await fetch(getApiUrl(`/api/matches/${id}/me`), { headers });
        const data = await res.json();
        if (process.env.NODE_ENV === 'development') {
  
        }
        if (data.isInMatch) {
          navigate(`/match/${id}`);
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('[DEBUG] Error consultando /api/matches/:id/me:', e);
        }
      }
    };
    checkAlreadyJoined();
  }, [id, navigate]);

  // Efecto separado para cargar datos inicialmente
  useEffect(() => {
    if (!id) return;
    fetchMatch();
    fetchGroupContext();
  }, [id, fetchMatch, fetchGroupContext]);

  // Fire once when match data first loads
  useEffect(() => {
    if (!match || !id || hasTrackedView.current) return;
    hasTrackedView.current = true;
    const guestCount = (match.players || []).filter((p: any) => !p.userId).length;
    track('invite_landing_viewed', {
      match_id: id,
      is_mobile: /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent || ''),
      guest_player_count: guestCount,
      total_players: (match.players || []).length,
      platform: 'web',
    });
  }, [match, id]);

  // Efecto separado para polling como fallback - solo si no hay WebSocket
  useEffect(() => {
    if (!id || isConnected) return; // No hacer polling si WebSocket está conectado
    
    const interval = setInterval(() => {
      fetchMatch();
    }, 30000); // Aumentar a 30 segundos
    
    return () => {
      clearInterval(interval);
    };
  }, [id, isConnected, fetchMatch]); // Remover fetchMatch de las dependencias

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${id}/join`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Almacenar el link de baja en localStorage
        if (result.leaveLink && result.player) {
          localStorage.setItem(`player_${result.player.id}_leaveLink`, result.leaveLink);
          localStorage.setItem(`player_${result.player.id}_name`, result.player.name);
          localStorage.setItem(`player_${result.player.id}_matchId`, id || '');
          // Guardar guestId para invitados
          const parts = result.leaveLink.split('/');
          const guestId = parts[parts.length - 1];
          localStorage.setItem(`guestId_${id}`, guestId);
        }
        
        setJoined(true);
        showNotification('¡Te has unido al partido exitosamente!', 'success');
        
        // Esperar un momento para que el WebSocket procese el evento antes de navegar
        setTimeout(() => {
          navigate(`/match/${id}`);
        }, 500);
      } else {
        const error = await response.json();
        showNotification(error.error || 'Error al unirse al partido', 'error');
      }
    } catch (error) {
      logger.error('Error joining match:', error);
      showNotification('Error al unirse al partido', 'error');
    } finally {
      setLoading(false);
    }
  };

  const maxTotal = match ? (match.maxTotalPlayers || match.maxPlayers * 2) : 0;
  const currentPlayers = match?.players?.filter((p: any) => !p.isSubstitute).length || 0;
  const spotsLeft = Math.max(0, maxTotal - currentPlayers);
  const isFull = spotsLeft === 0;

  const [guestName, setGuestName] = useState('');
  const [showGuestInput, setShowGuestInput] = useState(false);
  const [showGuestList, setShowGuestList] = useState(false);
  const [replacingGuestId, setReplacingGuestId] = useState<string | null>(null);

  const invitedPlayers = (match?.players || []).filter((p: any) => !p.userId);
  const canReplaceGuest = !!user && invitedPlayers.length > 0;
  const sameNameGuest = canReplaceGuest
    ? invitedPlayers.find((p: any) =>
        (p.name || '').trim().toLowerCase() === (user?.name || '').trim().toLowerCase()
      )
    : null;

  const handleReplaceGuest = async (playerId: string) => {
    if (!id || !user) return;
    setReplacingGuestId(playerId);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`/api/matches/${id}/replace-player`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          playerId,
          name: user.name || '',
          position: user.position || 'Mediocampista',
          rating: user.rating || 3,
        }),
      });

      if (response.ok) {
        track('invite_converted', { match_id: id, replaced_guest_id: playerId, platform: 'web' });
        showNotification('¡Reemplazaste al invitado con tu cuenta!', 'success');
        setTimeout(() => navigate(`/match/${id}`), 600);
      } else {
        const err = await response.json();
        showNotification(err.error || 'No se pudo reemplazar al invitado', 'error');
      }
    } catch (e) {
      logger.error('Error replacing guest:', e);
      showNotification('Error al reemplazar al invitado', 'error');
    } finally {
      setReplacingGuestId(null);
    }
  };

  // Handle "Me Sumo"
  const handleMeSumo = () => {
    if (user) {
      handleSubmit(new Event('submit') as any);
    } else {
      // Show guest name input instead of redirecting to auth
      setShowGuestInput(true);
    }
  };

  // Guest join (no auth required)
  const handleGuestJoin = async () => {
    if (!guestName.trim() || !id) return;
    setLoading(true);
    try {
      const response = await fetch(getApiUrl(`/api/matches/${id}/join`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: guestName.trim(),
          position: 'Mediocampista',
          rating: 3,
          isSubstitute: isFull,
        }),
      });
      if (response.ok) {
        const result = await response.json();
        if (result.leaveLink && result.player) {
          localStorage.setItem(`player_${result.player.id}_leaveLink`, result.leaveLink);
          localStorage.setItem(`player_${result.player.id}_name`, result.player.name);
          localStorage.setItem(`player_${result.player.id}_matchId`, id || '');
          try {
            const url = new URL(result.leaveLink);
            setGuestLeavePath(url.pathname);
          } catch {
            // leaveLink puede venir como path relativo
            setGuestLeavePath(result.leaveLink.startsWith('/') ? result.leaveLink : null);
          }
        }
        track('invite_guest_joined', { match_id: id, is_substitute: isFull, is_authenticated: false, platform: 'web' });
        setJoined(true);
        showNotification(isFull ? 'Estás en la lista de espera' : '¡Estás dentro!', 'success');
        // Guest: stay on this page (don't navigate to /match/ which requires auth)
        // Authenticated: navigate to match view
        if (user) {
          setTimeout(() => navigate(`/match/${id}`), 800);
        } else {
          // Refetch match to show updated player list
          fetchMatch();
        }
      } else {
        const err = await response.json();
        showNotification(err.error || 'Error al anotarse', 'error');
      }
    } catch {
      showNotification('Error al anotarse', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Renderizado
  return (
    <>
      <NotificationComponent
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />

      {showAppBanner && (
        <div
          role="banner"
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
            background: '#00C853', color: '#181A1B',
            padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: '8px',
            fontSize: '14px', fontWeight: 700,
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <span style={{ flex: 1 }}>¿Tenés Futbolero instalado? Abrilo en la app</span>
          <button
            onClick={handleOpenInApp}
            style={{
              padding: '6px 12px', borderRadius: '8px', border: 'none',
              background: '#181A1B', color: '#00E676',
              fontWeight: 700, fontSize: '13px', cursor: 'pointer',
            }}
          >
            Abrir
          </button>
          <button
            onClick={handleDismissAppBanner}
            aria-label="Cerrar banner"
            style={{
              padding: '4px 10px', borderRadius: '8px', border: 'none',
              background: 'transparent', color: '#181A1B',
              fontSize: '18px', lineHeight: 1, cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      )}

      {(!match) ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#181A1B', color: '#fff', paddingTop: showAppBanner ? '52px' : 0 }}>
          <p>Cargando partido...</p>
        </div>
      ) : joined ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#181A1B', color: '#fff', padding: '20px', paddingTop: showAppBanner ? '72px' : '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 8px', color: '#FFFFFF' }}>¡Estás dentro!</h1>
          <p style={{ color: '#B0BEC5', marginBottom: '16px' }}>{match.location} · {formatDate(match.date)} · {match.time}hs</p>

          {/* Show updated player count */}
          <p style={{ color: '#00E676', fontWeight: 700, fontSize: '16px', marginBottom: '24px' }}>
            {match.players?.filter((p: any) => !p.isSubstitute).length || 0}/{match.maxTotalPlayers || match.maxPlayers * 2} jugadores confirmados
          </p>

          {user ? (
            <button
              onClick={() => navigate(`/match/${match.id}`)}
              style={{ padding: '14px 32px', borderRadius: '12px', border: 'none', background: '#00E676', color: '#181A1B', fontWeight: 700, fontSize: '16px', cursor: 'pointer' }}
            >
              Ver el partido
            </button>
          ) : (
            <div style={{ width: '100%', maxWidth: '360px' }}>
              <p style={{ color: '#78909C', fontSize: '14px', marginBottom: '20px' }}>
                Listo, ya estás anotado. El organizador te va a ver en la lista.
              </p>

              {guestLeavePath && (
                <button
                  onClick={() => navigate(guestLeavePath)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '12px',
                    border: '1px solid #444', background: 'transparent',
                    color: '#B0BEC5', fontWeight: 600, fontSize: '14px',
                    cursor: 'pointer', marginBottom: '16px',
                  }}
                >
                  ❌ No puedo ir más → bajarme
                </button>
              )}

              {groupContext ? (
                <div style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, color: '#00E676', fontWeight: 700, marginBottom: 4 }}>🏃 {groupContext.name} · {groupContext.memberCount} miembros</div>
                  <p style={{ color: '#B0BEC5', fontSize: 13, margin: '0 0 12px 0' }}>
                    Con cuenta en Futbolero recibís el próximo partido de este grupo directo en la app. Sin WhatsApp.
                  </p>
                  <button
                    onClick={() => { saveJoinIntent(); navigate('/auth'); }}
                    style={{ width: '100%', padding: '10px 0', background: '#00E676', border: 'none', borderRadius: 10, color: '#181A1B', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
                  >
                    Unirme al grupo
                  </button>
                </div>
              ) : (
                <p style={{ color: '#78909C', fontSize: '13px', marginTop: '4px' }}>
                  ¿Querés ver quiénes más van?{' '}
                  <span
                    onClick={() => { saveJoinIntent(); navigate('/auth'); }}
                    style={{ color: '#00E676', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Creá tu cuenta
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ minHeight: '100vh', background: '#181A1B', color: '#fff', paddingTop: showAppBanner ? '46px' : 0 }}>
          {/* Public match preview - visible to everyone */}
          <div style={{ padding: '40px 20px 20px', textAlign: 'center', maxWidth: '480px', margin: '0 auto' }}>
            <div style={{ fontSize: '12px', color: '#00E676', fontWeight: 700, letterSpacing: '1px', marginBottom: '8px' }}>FUTBOLERO</div>
            <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 8px', color: '#fff' }}>{match.location}</h1>
            <p style={{ color: '#B0BEC5', fontSize: '16px', margin: '0 0 24px' }}>
              {formatDate(match.date)} · {match.time}hs
              {match.price > 0 && ` · $${Math.round(match.price / (match.maxTotalPlayers || match.maxPlayers * 2))} c/u`}
            </p>

            {/* Group context banner */}
            {groupContext && (
              <div style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)', borderRadius: 12, padding: '12px 14px', marginBottom: 16, textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#00E676', fontWeight: 700 }}>🏃 {groupContext.name}</div>
                  <div style={{ fontSize: 11, color: '#78909C' }}>{groupContext.memberCount} miembros del grupo</div>
                </div>
                {!user && (
                  <button
                    onClick={() => { saveJoinIntent(); navigate('/auth'); }}
                    style={{ padding: '6px 12px', background: '#00E676', border: 'none', borderRadius: 8, color: '#181A1B', fontWeight: 700, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Unirme →
                  </button>
                )}
              </div>
            )}

            {/* Player count bar */}
            <div style={{ background: '#2A2F33', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '18px' }}>{currentPlayers}/{maxTotal}</span>
                <span style={{ color: spotsLeft <= 5 && spotsLeft > 0 ? '#FF9800' : '#B0BEC5', fontWeight: 600, fontSize: '14px' }}>
                  {isFull ? 'Completo' : `Quedan ${spotsLeft} lugares`}
                </span>
              </div>
              <div style={{ background: '#1A1A1A', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                <div style={{ background: '#00E676', height: '100%', width: `${Math.min(100, (currentPlayers / maxTotal) * 100)}%`, borderRadius: '6px', transition: 'width 0.3s' }} />
              </div>
            </div>

            {/* Players list */}
            {match.players && match.players.length > 0 && (
              <div style={{ background: '#2A2F33', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
                <div style={{ fontSize: '12px', color: '#78909C', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.5px' }}>CONFIRMADOS</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {match.players.filter((p: any) => !p.isSubstitute).slice(0, 10).map((p: any, i: number) => (
                    <span key={i} style={{ background: '#1A1A1A', borderRadius: '8px', padding: '4px 10px', fontSize: '13px', color: '#E0E0E0' }}>
                      {p.name}
                    </span>
                  ))}
                  {currentPlayers > 10 && (
                    <span style={{ background: '#1A1A1A', borderRadius: '8px', padding: '4px 10px', fontSize: '13px', color: '#78909C' }}>
                      +{currentPlayers - 10} mas
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Pending group members with accumulated stats — FOMO for the claim flow */}
            {groupContext && groupContext.pendingMembers.length > 0 && (
              <div style={{ background: '#2A2F33', borderRadius: 12, padding: 16, marginBottom: 16, textAlign: 'left' }}>
                <div style={{ fontSize: 12, color: '#FFA500', fontWeight: 600, marginBottom: 10, letterSpacing: '0.5px' }}>EN LA LISTA SIN CUENTA</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groupContext.pendingMembers.map((pm) => (
                    <div key={pm.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18 }}>👻</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: '#E0E0E0', fontSize: 14 }}>{pm.guestName}</div>
                        {pm.totalMatches > 0 && (
                          <div style={{ fontSize: 12, color: '#78909C' }}>
                            {pm.totalMatches} partido{pm.totalMatches !== 1 ? 's' : ''} · {pm.totalGoals} ⚽ · {pm.totalAssists} 🅰️
                          </div>
                        )}
                      </div>
                      {!user && (
                        <button
                          onClick={() => { saveJoinIntent(); navigate(`/auth?claim_pending=${pm.id}&claim_group=${groupContext.id}`); }}
                          style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #FFA500', borderRadius: 8, color: '#FFA500', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          ¿Sos vos?
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!user && (
                  <p style={{ fontSize: 12, color: '#546E7A', marginTop: 10, marginBottom: 0 }}>
                    Reclamá tu perfil para ver tu historial y recibir notificaciones del grupo.
                  </p>
                )}
              </div>
            )}

            {/* Guest name input (shown after tapping "Me Sumo" without auth) */}
            {!user && showGuestInput ? (
              <div>
                <p style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>¿Cómo te llaman?</p>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Tu nombre"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '12px',
                    border: '1px solid #333',
                    background: '#2A2F33',
                    color: '#fff',
                    fontSize: '18px',
                    marginBottom: '12px',
                    boxSizing: 'border-box',
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && guestName.trim() && handleGuestJoin()}
                />
                <button
                  onClick={handleGuestJoin}
                  disabled={loading || !guestName.trim()}
                  style={{
                    width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
                    background: !guestName.trim() ? '#333' : '#00E676', color: '#181A1B',
                    fontWeight: 800, fontSize: '18px',
                    cursor: loading || !guestName.trim() ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1, marginBottom: '8px',
                  }}
                >
                  {loading ? 'Anotándote...' : '⚽ Listo, me anoto'}
                </button>
                <p style={{ fontSize: '13px', color: '#78909C', textAlign: 'center' }}>
                  Sin cuenta, solo tu nombre. Rápido y fácil.
                </p>
                <p style={{ fontSize: '13px', color: '#546E7A', textAlign: 'center', marginTop: '8px', cursor: 'pointer', textDecoration: 'underline' }}
                   onClick={() => { saveJoinIntent(); navigate('/auth'); }}>
                  ¿Ya tenés cuenta? Iniciá sesión
                </p>
              </div>
            ) : showGuestList ? (
              <div>
                <p style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginBottom: '12px', textAlign: 'left' }}>
                  ¿A qué invitado reemplazás?
                </p>
                {invitedPlayers.map((player: any) => {
                  const sameName = player.name?.toLowerCase() === (user?.name || '').toLowerCase();
                  const isReplacing = replacingGuestId === player.id;
                  return (
                    <button
                      key={player.id}
                      onClick={() => handleReplaceGuest(player.id)}
                      disabled={!!replacingGuestId}
                      style={{
                        width: '100%', padding: '14px 16px', borderRadius: '12px',
                        border: `1px solid ${sameName ? '#00E676' : '#FF9800'}`,
                        background: '#2A2F33', color: '#fff',
                        textAlign: 'left', marginBottom: '8px',
                        cursor: replacingGuestId ? 'not-allowed' : 'pointer',
                        opacity: replacingGuestId && !isReplacing ? 0.5 : 1,
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '4px' }}>
                        {isReplacing ? 'Reemplazando...' : player.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#B0BEC5' }}>
                        {player.isSubstitute ? 'Suplente' : 'Principal'}
                        {sameName ? ' · Mismo nombre que vos' : ''}
                      </div>
                    </button>
                  );
                })}
                <button
                  onClick={() => setShowGuestList(false)}
                  disabled={!!replacingGuestId}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '12px',
                    border: '1px solid #333', background: 'transparent', color: '#B0BEC5',
                    fontSize: '14px', fontWeight: 600, marginTop: '4px',
                    cursor: replacingGuestId ? 'not-allowed' : 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            ) : sameNameGuest ? (
              <>
                {/* Caso A: invitado con mismo nombre → confirmar */}
                <button
                  onClick={() => handleReplaceGuest(sameNameGuest.id)}
                  disabled={loading || !!replacingGuestId}
                  style={{
                    width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
                    background: '#00E676', color: '#181A1B',
                    fontWeight: 800, fontSize: '18px',
                    cursor: (loading || replacingGuestId) ? 'not-allowed' : 'pointer',
                    opacity: (loading || replacingGuestId) ? 0.7 : 1, marginBottom: '8px',
                  }}
                >
                  {replacingGuestId === sameNameGuest.id
                    ? 'Confirmando...'
                    : '✅ Soy yo, confirmar mi asistencia'}
                </button>
                <p style={{ fontSize: '13px', color: '#78909C', textAlign: 'center', marginBottom: '12px' }}>
                  El organizador te anotó como “{sameNameGuest.name}”.
                </p>
                <button
                  onClick={handleMeSumo}
                  disabled={loading || !!replacingGuestId}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '12px',
                    border: '1px solid #333', background: 'transparent', color: '#B0BEC5',
                    fontWeight: 600, fontSize: '14px',
                    cursor: (loading || replacingGuestId) ? 'not-allowed' : 'pointer',
                  }}
                >
                  Anotarme como otro jugador
                </button>
              </>
            ) : (
              <>
                {/* Caso B/C: CTA "Me Sumo" primario */}
                <button
                  onClick={handleMeSumo}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '16px', borderRadius: '14px', border: 'none',
                    background: isFull ? '#FF9800' : '#00E676', color: '#181A1B',
                    fontWeight: 800, fontSize: '18px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1, marginBottom: isFull ? '6px' : '12px',
                  }}
                >
                  {loading ? 'Anotándote...' : isFull ? '📋 Anotarme a la lista de espera' : '⚽ Me Sumo al Partido'}
                </button>

                {isFull && (
                  <p style={{ fontSize: '13px', color: '#78909C', textAlign: 'center', marginTop: 0, marginBottom: '12px' }}>
                    Te avisamos cuando se libere un lugar.
                  </p>
                )}

                {canReplaceGuest && (
                  <button
                    onClick={() => setShowGuestList(true)}
                    disabled={loading}
                    style={{
                      width: '100%', padding: '14px', borderRadius: '14px',
                      border: '1px solid #FF9800', background: 'transparent', color: '#FF9800',
                      fontWeight: 700, fontSize: '15px',
                      cursor: loading ? 'not-allowed' : 'pointer', marginBottom: '12px',
                    }}
                  >
                    El organizador ya me anotó{' '}
                    <span style={{ fontSize: '13px', opacity: 0.85 }}>
                      ({invitedPlayers.length})
                    </span>
                  </button>
                )}

                {!user && (
                  <p style={{ fontSize: '13px', color: '#78909C', marginTop: '4px', textAlign: 'center' }}>
                    Anotate con tu nombre, sin crear cuenta.
                  </p>
                )}
              </>
            )}
          </div>

        </div>
      )}
    </>
  );
};

export default JoinMatch; 